import { useCallback, useEffect, useState } from 'react'
import type {
  BusinessEntity,
  FinancialAccount,
  Property,
  PropertyInput,
  PropertyValuation,
  SourceResult,
} from '../../types/family-finance'
import { VALUATION_SOURCE_LABELS, categoryOf, workingValue } from '../../types/family-finance'
import {
  addProperty,
  deleteProperty,
  listAccounts,
  listEntities,
  listProperties,
  lookupProperty,
  refreshProperty,
} from '../../lib/family-finance-api'
import {
  Card,
  Chip,
  EmptyState,
  LoadError,
  LoadingRows,
  ProgressBar,
  StatCard,
  errorMessage,
  inputClass,
  money,
  primaryBtn,
  relativeTime,
  secondaryBtn,
  shortDate,
} from './shared'

/**
 * The CAD database's own county numbering, as LIVE PRODUCTION numbers them.
 *
 * Do not take these from tax-authority-db's `sql/schema.sql` seed: prod's
 * counties table predates that file, so several ids diverge — most sharply
 * Hays, which is 5 in prod and 8 in the repo's migration 008 (whose inserts
 * were no-ops against prod). Migration 025 spells the divergence out, and
 * ETPNext's `lib/counties.ts` carries the same verified map. Getting this
 * wrong silently labels a Hays house "Harris".
 *
 * An unmapped id falls back to the number rather than guessing a county.
 */
const COUNTY_NAMES: Record<number, string> = {
  1: 'Travis',
  2: 'Williamson',
  3: 'Bastrop',
  4: 'Burnet',
  5: 'Hays',
  9: 'Dallas',
  10: 'Bexar',
  11: 'Harris',
  12: 'Llano',
}

const SOURCE_LABELS: Record<'cad' | 'cma' | 'avm' | 'store', string> = {
  cad: 'Easy Tax Protest (CAD)',
  cma: 'Ironrock (CMA)',
  avm: 'RentCast (AVM)',
  store: 'Saving the refresh',
}

const SOURCE_ORDER = ['cad', 'cma', 'avm', 'store'] as const

/** Display order of the valuation chips: market opinions, then the tax roll. */
const CHIP_ORDER: string[] = [
  'manual',
  'cma',
  'lease',
  'avm',
  'cad-noticed',
  'cad-assessed',
  'cad',
]

/**
 * The front photo — the MLS listing's first image, or Google Street View when
 * the house has no listing on file — or a placeholder that doesn't pretend to
 * be one.
 *
 * `referrerPolicy="no-referrer"` is load-bearing: the Street View URL carries
 * Ironrock's Google Maps key, which is HTTP-referrer-restricted to
 * ironrockproperties.com. With this page as the referrer Google answers 403
 * and the photo silently fell to the placeholder; a referrer-less request is
 * accepted. The MLS proxy image loads either way.
 */
function PropertyPhoto({ url, alt }: { url: string | null | undefined; alt: string }) {
  const [failed, setFailed] = useState(false)
  if (!url || failed) {
    return (
      <div className="w-full h-36 rounded-lg bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center">
        <span className="text-slate-400 dark:text-slate-600 text-3xl">🏠</span>
      </div>
    )
  }
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => setFailed(true)}
      className="w-full h-36 object-cover rounded-lg border border-slate-200 dark:border-slate-700"
    />
  )
}

/**
 * The CMA's market census, in bucket order. Newer CMAs split the on-market
 * side into Active / Active Contingent / Pending; older snapshots carry the
 * combined 'contingent' bucket instead. Zero counts stay visible — "Pending 0"
 * is market information, not a missing field.
 */
const BUCKET_ORDER = ['active', 'activeContingent', 'pending', 'contingent', 'sold', 'leased'] as const
const BUCKET_LABELS: Record<(typeof BUCKET_ORDER)[number], string> = {
  active: 'Active',
  activeContingent: 'Active Contingent',
  pending: 'Pending',
  contingent: 'Contingent',
  sold: 'Sold',
  leased: 'Leased',
}

function bucketLine(counts: Record<string, number> | null | undefined): string | null {
  if (!counts) return null
  const parts = BUCKET_ORDER.filter((k) => typeof counts[k] === 'number').map(
    (k) => `${BUCKET_LABELS[k]} ${counts[k]}`
  )
  return parts.length ? parts.join(' · ') : null
}

/** One value with its source, range, market census and links. */
function ValuationChip({ valuation }: { valuation: PropertyValuation }) {
  // The lease quote is a monthly rent — say so everywhere a number shows.
  const perMonth = valuation.period === 'monthly' || valuation.source === 'lease'
  const suffix = perMonth ? '/mo' : ''
  const range =
    valuation.low && valuation.high
      ? `${money(valuation.low)}–${money(valuation.high)}${suffix}`
      : null
  const buckets = bucketLine(valuation.statusCounts)
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 min-w-0">
      <p className="text-slate-500 text-xs">
        {VALUATION_SOURCE_LABELS[valuation.source]}
        {valuation.compCount ? ` · ${valuation.compCount} comps` : ''}
        {valuation.taxYear ? ` · ${valuation.taxYear}` : ''}
        {valuation.thinSample ? ' · thin data' : ''}
      </p>
      <p className="text-slate-900 dark:text-white text-sm font-medium tabular-nums">
        {money(valuation.value)}
        {suffix}
      </p>
      {range && <p className="text-slate-500 text-xs tabular-nums">{range}</p>}
      {buckets && <p className="text-slate-500 text-xs tabular-nums">{buckets}</p>}
      {(valuation.reportUrl || valuation.pdfUrl) && (
        <p className="text-xs mt-0.5 flex gap-2">
          {valuation.reportUrl && (
            <a href={valuation.reportUrl} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
              report
            </a>
          )}
          {valuation.pdfUrl && (
            <a href={valuation.pdfUrl} target="_blank" rel="noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
              PDF
            </a>
          )}
        </p>
      )}
    </div>
  )
}

function emptyForm(): PropertyInput {
  return {
    label: '',
    address: '',
    city: '',
    zip: '',
    owner: 'Joint',
    entityId: null,
    linkedMortgageAccountId: null,
    purchasePrice: null,
    purchaseDate: null,
    homestead: false,
    livingAreaSqft: null,
    notes: '',
  }
}

/**
 * What each upstream said on the last pull.
 *
 * Shown even when everything worked, because the failure modes here are quiet
 * ones: the CAD covers four counties and reports an ambiguous situs as a miss,
 * and Ironrock declines an address it can't resolve. A property with no value
 * and no explanation reads as a house worth nothing.
 */
function SourceNote({ name, result }: { name: keyof typeof SOURCE_LABELS; result: SourceResult }) {
  if (result.status === 'ok') return null
  const tone = result.status === 'error' ? 'red' : result.status === 'not-found' ? 'amber' : 'slate'
  return (
    <div
      className={`rounded-lg px-3 py-2 border text-xs ${
        tone === 'red'
          ? 'bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400'
          : tone === 'amber'
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-600 dark:text-amber-400'
            : 'bg-slate-500/10 border-slate-500/30 text-slate-600 dark:text-slate-300'
      }`}
    >
      <span className="font-medium">{SOURCE_LABELS[name]}: </span>
      {result.status === 'not-configured' ? 'not connected on this deployment. ' : ''}
      {result.message}
    </div>
  )
}

function Fact({ label, value }: { label: string; value: string | null }) {
  if (!value) return null
  return (
    <div>
      <p className="text-slate-500 text-xs">{label}</p>
      <p className="text-slate-800 dark:text-slate-200 text-sm">{value}</p>
    </div>
  )
}

function PropertyCard({
  property,
  mortgage,
  entity,
  busy,
  onRefresh,
  onDelete,
}: {
  property: Property
  mortgage: FinancialAccount | undefined
  entity: BusinessEntity | undefined
  busy: boolean
  onRefresh: (property: Property) => void
  onDelete: (property: Property) => void
}) {
  const value = workingValue(property)
  const facts = property.facts
  // A mortgage is stored as a negative balance; equity is the value net of it.
  const owed = mortgage ? Math.abs(mortgage.balance) : 0
  const equity = value ? value.value - owed : null
  const valuations = [...(property.valuations ?? [])].sort(
    (a, b) => CHIP_ORDER.indexOf(a.source) - CHIP_ORDER.indexOf(b.source),
  )

  const baths =
    facts?.bathsFull != null
      ? `${facts.bathsFull}${facts.bathsHalf ? ` + ${facts.bathsHalf} half` : ''}`
      : null

  return (
    <Card className="!p-4">
      <div className="mb-3">
        <PropertyPhoto url={property.photoUrl} alt={property.label || property.address} />
      </div>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-slate-900 dark:text-white font-medium truncate">
            {property.label || property.address}
          </p>
          <p className="text-slate-500 text-xs truncate">
            {property.address}
            {property.city && `, ${property.city}`}
            {property.zip && ` ${property.zip}`}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1.5">
            <Chip tone={entity ? 'purple' : 'slate'}>
              {entity ? entity.shortName || entity.name : property.owner || 'Joint'}
            </Chip>
            {property.homestead && <Chip tone="green">homestead</Chip>}
            {facts?.countyId != null && (
              <Chip tone="blue">{COUNTY_NAMES[facts.countyId] || `County ${facts.countyId}`}</Chip>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          {value ? (
            <>
              <p className="text-slate-900 dark:text-white text-lg font-semibold tabular-nums">
                {money(value.value)}
              </p>
              <p className="text-slate-500 text-xs">
                {VALUATION_SOURCE_LABELS[value.source]} · {shortDate(value.asOf)}
              </p>
            </>
          ) : (
            <p className="text-slate-500 text-sm">No value yet</p>
          )}
        </div>
      </div>

      {/* Equity — only meaningful once both halves are known. */}
      {value && mortgage && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-500">
              Equity vs {mortgage.name} ({money(owed)} owed)
            </span>
            <span className={`font-medium ${equity && equity < 0 ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
              {money(equity ?? 0)}
            </span>
          </div>
          <ProgressBar
            value={Math.max(0, (equity ?? 0))}
            max={value.value}
            tone={equity && equity < 0 ? 'red' : 'green'}
          />
        </div>
      )}

      {/* CAD facts */}
      {facts && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3 pt-3 border-t border-slate-200/60 dark:border-slate-700/60">
          <Fact label="Living area" value={facts.sqft ? `${facts.sqft.toLocaleString()} sqft` : null} />
          <Fact label="Built" value={facts.yearBuilt ? String(facts.yearBuilt) : null} />
          <Fact label="Beds" value={facts.bedrooms != null ? String(facts.bedrooms) : null} />
          <Fact label="Baths" value={baths} />
          <Fact
            label="Lot"
            value={facts.lotSizeSqft ? `${facts.lotSizeSqft.toLocaleString()} sqft` : null}
          />
          <Fact label="CAD account" value={facts.parcelAccount} />
          <Fact label="Tax year" value={facts.taxYear ? String(facts.taxYear) : null} />
        </div>
      )}

      {/* Valuations — every value in one place, each chip naming its source:
          the Sale and Lease CMAs (Ironrock), the AVM (RentCast), and the tax
          noticed + assessed values (Easy Tax Protest CAD roll). */}
      {valuations.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
          {valuations.map((v) => (
            <ValuationChip key={v.source} valuation={v} />
          ))}
        </div>
      )}

      <div className="space-y-2 mb-3">
        {SOURCE_ORDER.map((name) => {
          const result = property.sources?.[name]
          return result ? <SourceNote key={name} name={name} result={result} /> : null
        })}
      </div>

      {property.notes && <p className="text-slate-500 text-xs mb-3">{property.notes}</p>}

      <div className="flex items-center justify-between gap-2">
        <p className="text-slate-500 text-xs">
          {property.lastRefresh ? `Refreshed ${relativeTime(property.lastRefresh)}` : 'Never refreshed'}
        </p>
        <div className="flex gap-2">
          <button onClick={() => onRefresh(property)} disabled={busy} className={secondaryBtn}>
            {busy ? 'Pulling…' : '↻ Refresh'}
          </button>
          <button
            onClick={() => onDelete(property)}
            disabled={busy}
            className="px-3 py-1.5 text-sm text-red-600/80 dark:text-red-400/80 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-500/10 rounded-lg transition-colors"
          >
            Remove
          </button>
        </div>
      </div>

      {/* Ironrock's licence terms travel with the number. */}
      {valuations.some((v) => v.source === 'cma' && v.disclaimer) && (
        <p className="text-slate-400 dark:text-slate-500 text-[11px] mt-3 pt-3 border-t border-slate-200/60 dark:border-slate-700/60">
          {valuations.find((v) => v.source === 'cma')?.attribution}{' '}
          {valuations.find((v) => v.source === 'cma')?.disclaimer}
        </p>
      )}
    </Card>
  )
}

function AddPropertyForm({
  entities,
  mortgages,
  onAdded,
  onCancel,
}: {
  entities: BusinessEntity[]
  mortgages: FinancialAccount[]
  onAdded: () => Promise<unknown>
  onCancel: () => void
}) {
  const [form, setForm] = useState<PropertyInput>(emptyForm())
  const [purchaseText, setPurchaseText] = useState('')
  const [areaText, setAreaText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [looking, setLooking] = useState(false)
  const [preview, setPreview] = useState<Partial<Property> | null>(null)

  /**
   * Fast load. Runs the same enrichment the save would run, but stores
   * nothing — so a wrong address costs a look rather than a property the owner
   * then has to delete, and the photo confirms it is the right house before
   * anything is kept.
   */
  async function lookAtIt() {
    if (!form.address.trim()) {
      setError('Type a street address first.')
      return
    }
    setLooking(true)
    setError(null)
    try {
      setPreview(
        await lookupProperty({
          address: form.address.trim(),
          city: form.city.trim() || undefined,
          zip: form.zip.trim() || undefined,
          livingAreaSqft: areaText ? parseInt(areaText, 10) : null,
        }),
      )
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setLooking(false)
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!form.address.trim()) {
      setError('A street address is required — it is what both lookups key on.')
      return
    }
    setBusy(true)
    setError(null)
    try {
      await addProperty({
        ...form,
        address: form.address.trim(),
        city: form.city.trim(),
        zip: form.zip.trim(),
        purchasePrice: purchaseText ? parseFloat(purchaseText) : null,
        livingAreaSqft: areaText ? parseInt(areaText, 10) : null,
      })
      await onAdded()
      onCancel()
    } catch (err) {
      setError(errorMessage(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title="Add a property" subtitle="The address is all that's required — the rest is looked up">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
          placeholder="Street address (e.g. 1601 Elmira Rd)"
          className={`${inputClass} sm:col-span-2`}
          autoFocus
        />
        <input
          value={form.city}
          onChange={(e) => setForm({ ...form, city: e.target.value })}
          placeholder="City"
          className={inputClass}
        />
        <input
          value={form.zip}
          onChange={(e) => setForm({ ...form, zip: e.target.value })}
          placeholder="ZIP"
          className={inputClass}
        />
        <input
          value={form.label}
          onChange={(e) => setForm({ ...form, label: e.target.value })}
          placeholder="Label (e.g. Homestead, Elmira rental)"
          className={inputClass}
        />
        <select
          value={form.entityId ?? ''}
          onChange={(e) => {
            const entityId = e.target.value || null
            const entity = entities.find((en) => en.id === entityId)
            setForm({
              ...form,
              entityId,
              owner: entity ? entity.shortName || entity.name : 'Joint',
            })
          }}
          className={inputClass}
        >
          <option value="">Owned personally (Joint)</option>
          {entities.map((en) => (
            <option key={en.id} value={en.id}>
              {en.shortName || en.name}
            </option>
          ))}
        </select>
        <select
          value={form.linkedMortgageAccountId ?? ''}
          onChange={(e) => setForm({ ...form, linkedMortgageAccountId: e.target.value || null })}
          className={inputClass}
        >
          <option value="">No mortgage linked</option>
          {mortgages.map((m) => (
            <option key={m.id} value={m.id}>
              {m.name} — {money(m.balance)}
            </option>
          ))}
        </select>
        <input
          value={purchaseText}
          onChange={(e) => setPurchaseText(e.target.value)}
          placeholder="Purchase price (optional)"
          type="number"
          step="0.01"
          className={inputClass}
        />
        <input
          value={form.purchaseDate ?? ''}
          onChange={(e) => setForm({ ...form, purchaseDate: e.target.value || null })}
          placeholder="Purchase date"
          type="date"
          className={inputClass}
        />
        <input
          value={areaText}
          onChange={(e) => setAreaText(e.target.value)}
          placeholder="Living area sqft (only if the CAD has no record)"
          type="number"
          className={inputClass}
        />
        <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 sm:col-span-2">
          <input
            type="checkbox"
            checked={form.homestead}
            onChange={(e) => setForm({ ...form, homestead: e.target.checked })}
            className="rounded"
          />
          Homestead exemption filed
        </label>
        {error && <p className="text-red-600 dark:text-red-400 text-sm sm:col-span-2">{error}</p>}

        {/* Fast-load result: what saving would store, before anything is stored. */}
        {preview && (
          <div className="sm:col-span-2 rounded-xl border border-slate-200 dark:border-slate-700 p-3 space-y-3">
            <div className="flex gap-3">
              <div className="w-40 flex-shrink-0">
                <PropertyPhoto url={preview.photoUrl} alt={form.address} />
              </div>
              <div className="min-w-0 flex-1">
                {preview.facts ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    <Fact
                      label="Living area"
                      value={preview.facts.sqft ? `${preview.facts.sqft.toLocaleString()} sqft` : null}
                    />
                    <Fact label="Built" value={preview.facts.yearBuilt ? String(preview.facts.yearBuilt) : null} />
                    <Fact
                      label="Beds"
                      value={preview.facts.bedrooms != null ? String(preview.facts.bedrooms) : null}
                    />
                    <Fact
                      label="Baths"
                      value={preview.facts.bathsFull != null ? String(preview.facts.bathsFull) : null}
                    />
                    <Fact
                      label="County"
                      value={
                        preview.facts.countyId != null
                          ? COUNTY_NAMES[preview.facts.countyId] || `County ${preview.facts.countyId}`
                          : null
                      }
                    />
                    <Fact label="CAD account" value={preview.facts.parcelAccount} />
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No county record matched this address.</p>
                )}
              </div>
            </div>

            {(preview.valuations ?? []).length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {(preview.valuations ?? []).map((v) => (
                  <ValuationChip key={v.source} valuation={v} />
                ))}
              </div>
            )}

            {SOURCE_ORDER.map((name) => {
              const result = preview.sources?.[name]
              return result ? <SourceNote key={name} name={name} result={result} /> : null
            })}
          </div>
        )}

        <div className="sm:col-span-2 flex items-center justify-between gap-3">
          <p className="text-slate-500 text-xs">
            {preview
              ? 'Saving stores exactly what you see here.'
              : 'Look it up first to see the house before you keep it — nothing is saved until you add it.'}
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onCancel} className={secondaryBtn}>
              Cancel
            </button>
            <button type="button" onClick={lookAtIt} disabled={looking || busy} className={secondaryBtn}>
              {looking ? 'Looking up…' : '⚡ Look up'}
            </button>
            <button type="submit" disabled={busy || looking} className={primaryBtn}>
              {busy ? 'Saving…' : preview ? 'Save property' : 'Add & look up'}
            </button>
          </div>
        </div>
      </form>
    </Card>
  )
}

export default function PropertiesTab() {
  const [propertyList, setPropertyList] = useState<Property[]>([])
  const [accounts, setAccounts] = useState<FinancialAccount[]>([])
  const [entities, setEntities] = useState<BusinessEntity[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const reload = useCallback(async () => {
    const [props, accts, ents] = await Promise.all([listProperties(), listAccounts(), listEntities()])
    setPropertyList(props)
    setAccounts(accts)
    setEntities(ents)
    setLoading(false)
  }, [])

  useEffect(() => {
    reload()
      .then(() => setLoadError(null))
      .catch((err) => {
        setLoadError(errorMessage(err))
        setLoading(false)
      })
  }, [reload])

  async function handleRefresh(property: Property) {
    setBusyId(property.id)
    setActionError(null)
    try {
      const updated = await refreshProperty(property.id)
      setPropertyList((rows) => rows.map((p) => (p.id === updated.id ? updated : p)))
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  async function handleDelete(property: Property) {
    if (!window.confirm(`Remove ${property.label || property.address} from the dashboard?`)) return
    setBusyId(property.id)
    setActionError(null)
    try {
      await deleteProperty(property.id)
      setPropertyList((rows) => rows.filter((p) => p.id !== property.id))
    } catch (err) {
      setActionError(errorMessage(err))
    } finally {
      setBusyId(null)
    }
  }

  if (loadError) {
    return (
      <LoadError
        error={loadError}
        onRetry={() => {
          setLoading(true)
          setLoadError(null)
          reload().catch((err) => {
            setLoadError(errorMessage(err))
            setLoading(false)
          })
        }}
      />
    )
  }
  if (loading) return <LoadingRows rows={4} />

  const mortgages = accounts.filter((a) => categoryOf(a.type) === 'mortgage')
  const valued = propertyList.map((p) => ({ property: p, valuation: workingValue(p) }))
  const totalValue = valued.reduce((s, v) => s + (v.valuation?.value ?? 0), 0)
  const linkedDebt = valued.reduce((s, { property }) => {
    const mortgage = accounts.find((a) => a.id === property.linkedMortgageAccountId)
    return s + (mortgage ? Math.abs(mortgage.balance) : 0)
  }, 0)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {propertyList.length} propert{propertyList.length === 1 ? 'y' : 'ies'} · facts + tax values
          from Easy Tax Protest, sale &amp; lease CMAs from Ironrock
        </p>
        {!adding && (
          <button onClick={() => setAdding(true)} className={primaryBtn}>
            + Add property
          </button>
        )}
      </div>

      {actionError && <LoadError error={actionError} />}

      {adding && (
        <AddPropertyForm
          entities={entities}
          mortgages={mortgages}
          onAdded={reload}
          onCancel={() => setAdding(false)}
        />
      )}

      {propertyList.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Property Value" value={money(totalValue)} sub="Working value, best source per property" />
          <StatCard label="Mortgage Debt" value={money(linkedDebt)} sub="Linked mortgage balances" />
          <StatCard
            label="Equity"
            value={money(totalValue - linkedDebt)}
            subTone={totalValue - linkedDebt >= 0 ? 'green' : 'red'}
            sub="Value less what's owed on it"
          />
        </div>
      )}

      {propertyList.length === 0 && !adding && (
        <Card>
          <EmptyState
            icon="🏠"
            message="No properties yet. Add a street address and the dashboard pulls the county's record of the house and a current CMA."
          />
          <p className="text-slate-500 text-xs text-center max-w-xl mx-auto">
            The appraisal-district lookup covers the counties loaded in the Easy Tax Protest database;
            the CMA comes from Ironrock's replicated Unlock MLS sold index, so it works across the
            Austin metro.
          </p>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {propertyList.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            mortgage={accounts.find((a) => a.id === property.linkedMortgageAccountId)}
            entity={entities.find((e) => e.id === property.entityId)}
            busy={busyId === property.id}
            onRefresh={handleRefresh}
            onDelete={handleDelete}
          />
        ))}
      </div>
    </div>
  )
}
